/**
 * Servicio de Autenticación
 * Conecta con el backend de autenticación en localhost:5000
 * Gestiona tokens JWT en localStorage
 */

// URL del backend — runtime (window.__ENV__) tiene prioridad sobre build-time (VITE_)
// En producción start.sh inyecta AUTH_API_URL en /dist/env.js antes de que Caddy arranque
const AUTH_API =
  (window as unknown as { __ENV__?: { AUTH_API_URL?: string } }).__ENV__?.AUTH_API_URL ||
  import.meta.env.VITE_AUTH_API_URL ||
  "http://localhost:5010/api/v1";
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "auth_user";
const COMPANY_KEY = "auth_company";
const BRANCHES_KEY = "auth_branches";
const REMEMBER_ME_KEY = "remember_me";

export interface AuthUser {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role_id: number;
  role_name: string;
  company_id?: number;
  branch_id?: number;
  is_active: boolean;
  created_at?: string;
  last_login?: string;
}

export interface Company {
  id: number;
  name: string;
  rfc?: string;
  is_active: boolean;
}

export interface Branch {
  id: number;
  name: string;
  company_id: number;
  address?: string;
  is_active: boolean;
}

export interface LoginResponse {
  success: boolean;
  data: {
    user: AuthUser;
    tokens: {
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    };
    company?: Company;
    branches?: Branch[];
  };
  message: string;
}

export interface AuthError {
  message: string;
  code?: string;
}

/**
 * Login con username y contraseña
 * Retorna tokens y datos del usuario
 */
export async function login(
  username: string,
  password: string,
  rememberMe: boolean = false
): Promise<LoginResponse> {
  try {
    // Endpoint dedicado: la restricción de sysadmin se enforce en el servidor
    const response = await fetch(`${AUTH_API}/auth/admin/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Credenciales inválidas");
    }

    const result: LoginResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || "Error de autenticación");
    }

    // Guardar tokens y usuario en localStorage
    localStorage.setItem(ACCESS_TOKEN_KEY, result.data.tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, result.data.tokens.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(result.data.user));
    
    // Guardar company y branches si existen
    if (result.data.company) {
      localStorage.setItem(COMPANY_KEY, JSON.stringify(result.data.company));
    }
    if (result.data.branches) {
      localStorage.setItem(BRANCHES_KEY, JSON.stringify(result.data.branches));
    }

    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, "true");
    }

    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Error de autenticación";
    throw { message, code: "LOGIN_ERROR" } as AuthError;
  }
}

/**
 * Logout - Limpia la sesión local
 */
export async function logout(): Promise<void> {
  const token = getToken();

  try {
    if (token) {
      await fetch(`${AUTH_API}/auth/logout`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
    }
  } catch (error) {
    console.error("Error during logout:", error);
  } finally {
    // Limpiar localStorage independientemente del resultado
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(BRANCHES_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
  }
}

/**
 * Obtener access token JWT del localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Obtener refresh token del localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Obtener usuario actual del localStorage
 */
export function getCurrentUser(): AuthUser | null {
  const user = localStorage.getItem(USER_KEY);
  if (!user) return null;

  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
}

/**
 * Obtener company actual del localStorage
 */
export function getCurrentCompany(): Company | null {
  const company = localStorage.getItem(COMPANY_KEY);
  if (!company) return null;

  try {
    return JSON.parse(company);
  } catch {
    return null;
  }
}

/**
 * Obtener branches del localStorage
 */
export function getBranches(): Branch[] {
  const branches = localStorage.getItem(BRANCHES_KEY);
  if (!branches) return [];

  try {
    return JSON.parse(branches);
  } catch {
    return [];
  }
}

/**
 * Verificar si el usuario está autenticado
 */
export function isAuthenticated(): boolean {
  return !!getToken() && !!getCurrentUser();
}

/**
 * Verificar si la sesión debe ser recordada
 */
export function shouldRememberMe(): boolean {
  return localStorage.getItem(REMEMBER_ME_KEY) === "true";
}

/**
 * Obtener headers con autenticación
 * Incluye el token JWT en el header Authorization
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token && { "Authorization": `Bearer ${token}` }),
  };
}

/**
 * Hacer petición autenticada al backend de auth
 * Incluye automáticamente el token JWT
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  return fetch(`${AUTH_API}${endpoint}`, {
    ...options,
    headers,
  });
}

/**
 * Validar token con el servidor
 * Retorna los datos del usuario si el token es válido
 */
export async function validateToken(): Promise<AuthUser | null> {
  try {
    const response = await authenticatedFetch("/auth/me");

    if (!response.ok) {
      // Token inválido o expirado
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(COMPANY_KEY);
      localStorage.removeItem(BRANCHES_KEY);
      return null;
    }

    const result = await response.json();
    
    // El backend retorna { success: true, data: {...} }
    if (result.success && result.data) {
      return result.data;
    }

    return null;
  } catch (error) {
    console.error("Error validating token:", error);
    return null;
  }
}

/**
 * Verificar credenciales del usuario sin modificar la sesión activa.
 * Útil para re-autenticación antes de operaciones destructivas.
 *
 * @returns true si las credenciales son correctas, false si son incorrectas.
 * @throws Error si hay un problema de red inesperado.
 */
export async function verifyCredentials(
  username: string,
  password: string
): Promise<boolean> {
  try {
    const response = await fetch(`${AUTH_API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    if (response.status === 401 || response.status === 403) return false;
    if (response.ok) return true;

    // Otro código de error => propagar
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Error al verificar credenciales");
  } catch (error) {
    if (error instanceof Error && error.message !== "Error al verificar credenciales") {
      throw error;
    }
    return false;
  }
}

/**
 * Refrescar access token usando refresh token
 */
export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${AUTH_API}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    
    if (result.success && result.data.access_token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, result.data.access_token);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return false;
  }
}
